// src/pages/SocialPage.js
import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useOutletContext } from 'react-router-dom'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

/* ------------------------------------------------------------------ *
 * 0.  Config                                                         *
 * ------------------------------------------------------------------ */
// Adjust BACKEND_URL as needed or use a .env variable
const BACKEND_URL =
  'https://payme-backend-713827320947.asia-northeast1.run.app'

/* ------------------------------------------------------------------ *
 * 1.  UI translations                                                *
 * ------------------------------------------------------------------ */
const translations = {
  en: {
    pageTitle: 'Social Events',
    addEvent: 'Add Event',
    eventName: 'Name',
    eventDate: 'Date',
    participants: 'Participants',
    totalAmount: 'Amount',
    numberPeople: 'People',
    totalReceived: 'Received',
    status: 'Status',
    ongoing: 'Ongoing',
    closed: 'Closed',
    overview: 'Overview',
    trackOption: 'Track',
    editOption: 'Edit',
    cancelOption: 'Cancel',
    confirmCancel: 'Are you sure you want to cancel this event?',
    confirmYes: 'Yes, cancel',
    createEvent: 'Create Event',
    modalTitleAdd: 'Create a New Event',
    required: 'Required',
    placeholderParticipants: 'Type participant email...',
    eventNamePlaceholder: 'e.g. Birthday Dinner',
    eventDatePlaceholder: 'Select date',
    currencySymbol: '¥',
    cancel: 'Cancel',
    inviteFriendLabel: 'Invite your friend',
    noEventsPlaceholder: 'No events yet. Click “Add Event” to create one.',
    trackModalTitle: 'Participants',
    participantCol: 'Participant',
    amountCol: 'Amount',
    statusCol: 'Status',
    paidStatus: 'Paid',
    pendingStatus: 'Pending',
    minAmountMsg: 'The minimum amount for your event is 50 yen.',
    modalTitleEdit: 'Add Participants to Event',
    addParticipantBtn: 'Add',
    saveChangesBtn: 'Save Changes',
    editParticipantAmountPlaceholder: 'Enter amount for this participant',
  },
  ja: {
    pageTitle: 'ソーシャルイベント',
    addEvent: 'イベントを追加',
    eventName: '名前',
    eventDate: '日付',
    participants: '参加者',
    totalAmount: '金額',
    numberPeople: '人数',
    totalReceived: '受取済',
    status: 'ステータス',
    ongoing: '進行中',
    closed: '完了',
    overview: '概要',
    trackOption: 'トラック',
    editOption: '編集',
    cancelOption: 'キャンセル',
    confirmCancel: 'このイベントをキャンセルしますか？',
    confirmYes: 'はい、キャンセル',
    createEvent: 'イベントを作成',
    modalTitleAdd: '新しいイベントを作成',
    required: '必須',
    placeholderParticipants: 'メールアドレスを入力...',
    eventNamePlaceholder: '例： 誕生日ディナー',
    eventDatePlaceholder: '日付を選択',
    currencySymbol: '¥',
    cancel: 'キャンセル',
    inviteFriendLabel: '友達を招待',
    noEventsPlaceholder: 'イベントはまだありません。「イベントを追加」をクリックしてください。',
    trackModalTitle: '参加者一覧',
    participantCol: '参加者',
    amountCol: '金額',
    statusCol: 'ステータス',
    paidStatus: '支払済',
    pendingStatus: '未払い',
    minAmountMsg: 'イベントの最低金額は50円です。',
    modalTitleEdit: 'イベントに参加者を追加',
    addParticipantBtn: '追加',
    saveChangesBtn: '変更を保存',
    editParticipantAmountPlaceholder: '参加者の支払額を入力してください',
  },
  pt: {
    pageTitle: 'Eventos Sociais',
    addEvent: 'Adicionar Evento',
    eventName: 'Nome',
    eventDate: 'Data',
    participants: 'Participantes',
    totalAmount: 'Valor',
    numberPeople: 'Pessoas',
    totalReceived: 'Recebido',
    status: 'Status',
    ongoing: 'Em andamento',
    closed: 'Fechado',
    overview: 'Visão Geral',
    trackOption: 'Acompanhar',
    editOption: 'Editar',
    cancelOption: 'Cancelar',
    confirmCancel: 'Tem certeza de que deseja cancelar este evento?',
    confirmYes: 'Sim, cancelar',
    createEvent: 'Criar Evento',
    modalTitleAdd: 'Criar novo evento',
    required: 'Obrigatório',
    placeholderParticipants: 'Digite o email...',
    eventNamePlaceholder: 'ex: Jantar de Aniversário',
    eventDatePlaceholder: 'Selecione a data',
    currencySymbol: 'R$',
    cancel: 'Cancelar',
    inviteFriendLabel: 'Convide seu amigo',
    noEventsPlaceholder: 'Nenhum evento ainda. Clique em “Adicionar Evento” para criar um.',
    trackModalTitle: 'Participantes',
    participantCol: 'Participante',
    amountCol: 'Valor',
    statusCol: 'Status',
    paidStatus: 'Pago',
    pendingStatus: 'Pendente',
    minAmountMsg: 'O valor mínimo para o seu evento é 50 ienes.',
    modalTitleEdit: 'Adicionar participantes ao evento',
    addParticipantBtn: 'Adicionar',
    saveChangesBtn: 'Salvar alterações',
    editParticipantAmountPlaceholder: 'Digite o valor para este participante',
  },
}

/* ------------------------------------------------------------------ *
 * 2.  Component                                                      *
 * ------------------------------------------------------------------ */
function SocialPage() {
  /* ---------------------------------- *
   * 2-A.  Auth / language              *
   * ---------------------------------- */
  const storedUser = JSON.parse(localStorage.getItem('user'))
  const userId = storedUser ? storedUser.id : null
  const { lang } = useOutletContext()
  const t = translations[lang] || translations.en

  /* ---------------------------------- *
   * 2-B.  State buckets                *
   * ---------------------------------- */
  const [events, setEvents] = useState([])

  /* Add-modal */
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)
  const [newEvent, setNewEvent] = useState({
    name: '',
    participants: [],
    totalAmount: '',
  })
  const [amountWarning, setAmountWarning] = useState('')
  const [participantInput, setParticipantInput] = useState('')
  const [emailSuggestions, setEmailSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)

  /* Track-modal */
  const [trackModalOpen, setTrackModalOpen] = useState(false)
  const [trackEvent, setTrackEvent] = useState(null)
  const [trackParticipants, setTrackParticipants] = useState([])

  /* Edit-modal */
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editEvent, setEditEvent] = useState(null)
  const [editNewParticipants, setEditNewParticipants] = useState([])
  const [editParticipantEmail, setEditParticipantEmail] = useState('')
  const [editParticipantAmount, setEditParticipantAmount] = useState('')
  const [editEmailSuggestions, setEditEmailSuggestions] = useState([])
  const [showEditSuggestions, setShowEditSuggestions] = useState(false)
  const [editErrorMsg, setEditErrorMsg] = useState('')

  /* Cancel-confirm modal */
  const [cancelTarget, setCancelTarget] = useState(null)

  /* ------------------------------------------------------------------ *
   * 3.  Initial fetch                                                  *
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (!userId) return
    axios
      .get(`${BACKEND_URL}/api/social/events?ownerId=${userId}`)
      .then((res) => setEvents(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('Error fetching events:', err))
  }, [userId])

  /* ------------------------------------------------------------------ *
   * 4.  Autocomplete helpers (Add)                                     *
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (participantInput.trim().length < 2) {
      setEmailSuggestions([])
      setShowSuggestions(false)
      return
    }
    setShowSuggestions(true)

    const fetchEmails = async () => {
      try {
        const resp = await axios.get(
          `${BACKEND_URL}/api/users/search?query=${encodeURIComponent(
            participantInput.trim()
          )}`
        )
        setEmailSuggestions(
          Array.isArray(resp.data) && resp.data.length
            ? resp.data
            : [{ id: 'inviteFriend', email: '' }]
        )
      } catch (err) {
        console.error('Email search error:', err)
        setEmailSuggestions([])
      }
    }
    fetchEmails()
  }, [participantInput])

  const formatSuggestion = (item) =>
    item.id === 'inviteFriend'
      ? t.inviteFriendLabel
      : item.name
      ? `${item.name} - ${item.email}`
      : item.email

  const handleSelectSuggestion = (item) => {
    if (item.id === 'inviteFriend') {
      setShowSuggestions(false)
      return
    }
    setNewEvent((p) => ({
      ...p,
      participants: [...p.participants, item.email],
    }))
    setParticipantInput('')
    setEmailSuggestions([])
    setShowSuggestions(false)
  }

  /* ------------------------------------------------------------------ *
   * 5.  Field helpers                                                  *
   * ------------------------------------------------------------------ */
  const handleTotalAmountChange = (e) => {
    const val = e.target.value
    setNewEvent((prev) => ({ ...prev, totalAmount: val }))
    const numericVal = parseInt(val, 10)
    setAmountWarning(
      !isNaN(numericVal) && numericVal > 0 && numericVal < 50
        ? t.minAmountMsg
        : ''
    )
  }

  /* ------------------------------------------------------------------ *
   * 6.  CRUD  ───────────────────────────────────────────────────────── *
   * ------------------------------------------------------------------ */
  /* 6-A.  Create event */
  const handleCreateEvent = async () => {
    if (!userId) return alert('No user')

    const { name, participants, totalAmount } = newEvent
    if (!name || !selectedDate || !participants.length || !totalAmount) {
      return alert(t.required)
    }
    const parsedAmount = parseInt(totalAmount, 10)
    if (isNaN(parsedAmount) || parsedAmount < 50) {
      return alert(t.minAmountMsg)
    }

    const eventDate = selectedDate.toISOString().slice(0, 10)
    const payload = {
      ownerId: userId,
      name,
      eventDate,
      totalAmount: parsedAmount,
      participantEmails: participants,
      ownerReceives: true,
    }

    try {
      await axios.post(`${BACKEND_URL}/api/social/events`, payload)
      const res = await axios.get(
        `${BACKEND_URL}/api/social/events?ownerId=${userId}`
      )
      setEvents(Array.isArray(res.data) ? res.data : [])
      setIsAddModalOpen(false)
    } catch (err) {
      console.error('Create event error:', err)
      alert('Failed to create event.')
    }
  }

  /* 6-B.  Cancel event (uses confirmation modal) */
  const cancelModalClose = () => setCancelTarget(null)

  const doCancelEvent = async () => {
    if (!cancelTarget) return
    try {
      await axios.delete(`${BACKEND_URL}/api/social/events/${cancelTarget.id}`)
      const res = await axios.get(
        `${BACKEND_URL}/api/social/events?ownerId=${userId}`
      )
      setEvents(Array.isArray(res.data) ? res.data : [])
    } catch (err) {
      console.error('Cancel event error:', err)
      alert('Failed to cancel event.')
    } finally {
      cancelModalClose()
    }
  }

  /* 6-C.  Track */
  const openTrackModal = async (evt) => {
    setTrackEvent(evt)
    setTrackModalOpen(true)
    setTrackParticipants([])
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/social/participants?eventId=${evt.id}`
      )
      setTrackParticipants(Array.isArray(res.data) ? res.data : [])
    } catch (error) {
      console.error('Track fetch error:', error)
    }
  }
  const closeTrackModal = () => {
    setTrackModalOpen(false)
    setTrackParticipants([])
  }

  /* 6-D.  Edit (open / close helpers only; save handler unchanged) */
  const openEditModal = (evt) => {
    setEditEvent(evt)
    setIsEditModalOpen(true)
    setEditNewParticipants([])
    setEditParticipantEmail('')
    setEditParticipantAmount('')
    setEditEmailSuggestions([])
    setShowEditSuggestions(false)
    setEditErrorMsg('')
  }
  const closeEditModal = () => {
    setIsEditModalOpen(false)
    setEditErrorMsg('')
  }

  /* ------------------------------------------------------------------ *
   * 7.  Autocomplete helpers (Edit)                                    *
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (!isEditModalOpen) return
    if (editParticipantEmail.trim().length < 2) {
      setEditEmailSuggestions([])
      setShowEditSuggestions(false)
      return
    }
    setShowEditSuggestions(true)

    const fetchEditEmails = async () => {
      try {
        const resp = await axios.get(
          `${BACKEND_URL}/api/users/search?query=${encodeURIComponent(
            editParticipantEmail.trim()
          )}`
        )
        setEditEmailSuggestions(
          Array.isArray(resp.data) && resp.data.length
            ? resp.data
            : [{ id: 'inviteFriend', email: '' }]
        )
      } catch (err) {
        console.error('Edit email search error:', err)
        setEditEmailSuggestions([])
      }
    }
    fetchEditEmails()
  }, [editParticipantEmail, isEditModalOpen])

  const formatEditSuggestion = (item) =>
    item.id === 'inviteFriend'
      ? t.inviteFriendLabel
      : item.name
      ? `${item.name} - ${item.email}`
      : item.email

  const handleSelectEditSuggestion = (item) => {
    if (item.id === 'inviteFriend') {
      setShowEditSuggestions(false)
      return
    }
    setEditParticipantEmail(item.email)
    setEditEmailSuggestions([])
    setShowEditSuggestions(false)
  }

  /* ------------------------------------------------------------------ *
   * 8.  Edit helper actions                                            *
   * ------------------------------------------------------------------ */
  const handleAddEditParticipant = () => {
    setEditErrorMsg('')
    if (!editParticipantEmail || !editParticipantAmount) {
      return setEditErrorMsg(t.required)
    }
    const amt = parseInt(editParticipantAmount, 10)
    if (isNaN(amt) || amt < 50) {
      return setEditErrorMsg(t.minAmountMsg)
    }
    setEditNewParticipants((p) => [
      ...p,
      { email: editParticipantEmail.trim(), amount: amt },
    ])
    setEditParticipantEmail('')
    setEditParticipantAmount('')
  }

  const handleSaveEdit = async () => {
    if (!editEvent) return
    if (editNewParticipants.length === 0)
      return setEditErrorMsg('No participants to add.')
    try {
      await axios.post(
        `${BACKEND_URL}/api/social/events/${editEvent.id}/add-participants`,
        { newParticipants: editNewParticipants }
      )
      const res = await axios.get(
        `${BACKEND_URL}/api/social/events?ownerId=${userId}`
      )
      setEvents(Array.isArray(res.data) ? res.data : [])
      if (trackEvent && trackEvent.id === editEvent.id) {
        openTrackModal(trackEvent) // refresh if open
      }
      closeEditModal()
    } catch (err) {
      console.error('Add participants error:', err)
      setEditErrorMsg('Failed to add participants.')
    }
  }

  /* ------------------------------------------------------------------ *
   * 9.  Render                                                         *
   * ------------------------------------------------------------------ */
  return (
    <div className="max-w-screen-lg mx-auto px-2 py-4 space-y-4">
      {/* ─────────────────────── Main card ─────────────────────── */}
      <div className="card bg-white border border-gray-200 rounded shadow-sm text-sm">
        <div className="card-header border-b border-dashed p-4 flex items-center justify-between">
          <h5 className="text-base md:text-lg font-semibold text-gray-700">
            {t.pageTitle}
          </h5>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-blue-600 text-white px-3 py-2 rounded shadow hover:bg-blue-700 text-sm"
          >
            {t.addEvent}
          </button>
        </div>

        <div className="card-body p-4">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-gray-600 text-xs md:text-sm">
              <thead className="uppercase bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-2 md:px-4 py-2 md:py-3">
                    {t.eventName}
                  </th>
                  <th className="px-2 md:px-4 py-2 md:py-3">
                    {t.eventDate}
                  </th>
                  <th className="px-2 md:px-4 py-2 md:py-3">
                    {t.numberPeople}
                  </th>
                  <th className="px-2 md:px-4 py-2 md:py-3">
                    {t.totalAmount}
                  </th>
                  <th className="px-2 md:px-4 py-2 md:py-3">
                    {t.totalReceived}
                  </th>
                  <th className="px-2 md:px-4 py-2 md:py-3">
                    {t.status}
                  </th>
                  <th className="px-2 md:px-4 py-2 md:py-3">
                    {t.overview}
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {events.map((evt) => {
                  const dateString =
                    typeof evt.event_date === 'string'
                      ? evt.event_date.slice(0, 10)
                      : evt.event_date

                  const isClosed =
                    Number(evt.total_received) >= Number(evt.total_amount)

                  const statusLabel = isClosed ? t.closed : t.ongoing
                  const statusClass = isClosed
                    ? 'inline-block px-2 py-1 text-xs font-semibold bg-red-100 text-red-600 rounded'
                    : 'inline-block px-2 py-1 text-xs font-semibold bg-green-100 text-green-600 rounded'

                  return (
                    <tr key={evt.id} className="hover:bg-gray-50">
                      <td className="px-2 md:px-4 py-2 md:py-3">
                        {evt.name}
                      </td>
                      <td className="px-2 md:px-4 py-2 md:py-3">
                        {dateString}
                      </td>
                      <td className="px-2 md:px-4 py-2 md:py-3">
                        {evt.num_participants || 0}
                      </td>
                      <td className="px-2 md:px-4 py-2 md:py-3">
                        {t.currencySymbol}
                        {evt.total_amount}
                      </td>
                      <td className="px-2 md:px-4 py-2 md:py-3">
                        {t.currencySymbol}
                        {evt.total_received || 0}
                      </td>
                      <td className="px-2 md:px-4 py-2 md:py-3">
                        <span className={statusClass}>{statusLabel}</span>
                      </td>
                      <td className="px-2 md:px-4 py-2 md:py-3 whitespace-nowrap">
                        <button
                          onClick={() => openTrackModal(evt)}
                          className="text-blue-600 hover:underline"
                        >
                          {t.trackOption}
                        </button>

                        <button
                          onClick={() => openEditModal(evt)}
                          className="text-blue-600 hover:underline ml-3"
                        >
                          {t.editOption}
                        </button>

                        <button
                          onClick={() => setCancelTarget(evt)}
                          className="text-red-600 hover:underline ml-3"
                        >
                          {t.cancelOption}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {events.length === 0 && (
              <p className="mt-4 text-gray-500 text-center">
                {t.noEventsPlaceholder}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ─────────────────────── ADD EVENT MODAL ─────────────────────── */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-2">
          <div className="bg-white w-full max-w-lg rounded shadow p-4 md:p-6 relative text-sm">
            <button
              onClick={() => setIsAddModalOpen(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
            >
              &times;
            </button>

            <h3 className="text-base md:text-xl font-semibold mb-4">
              {t.modalTitleAdd}
            </h3>

            {/* --- Name --- */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                {t.eventName}
              </label>
              <input
                type="text"
                className="border w-full px-2 py-2 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder={t.eventNamePlaceholder}
                value={newEvent.name}
                onChange={(e) =>
                  setNewEvent((p) => ({ ...p, name: e.target.value }))
                }
              />
            </div>

            {/* --- Date --- */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                {t.eventDate}
              </label>
              <DatePicker
                selected={selectedDate}
                onChange={setSelectedDate}
                dateFormat="yyyy-MM-dd"
                placeholderText={t.eventDatePlaceholder}
                className="border w-full px-2 py-2 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* --- Participants input --- */}
            <div className="mb-3 relative">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                {t.participants}
              </label>
              <input
                type="text"
                className="border w-full px-2 py-2 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder={t.placeholderParticipants}
                value={participantInput}
                onChange={(e) => setParticipantInput(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
              />

              {showSuggestions && emailSuggestions.length > 0 && (
                <div className="absolute z-50 bg-white border border-gray-300 rounded mt-1 w-full max-h-32 overflow-y-auto shadow-md text-sm">
                  {emailSuggestions.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleSelectSuggestion(item)}
                      className="px-2 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      {formatSuggestion(item)}
                    </div>
                  ))}
                </div>
              )}

              {/* Chips */}
              <div className="flex flex-wrap gap-2 mt-2">
                {newEvent.participants.map((email, idx) => (
                  <div
                    key={idx}
                    className="bg-blue-100 text-blue-600 px-2 py-1 rounded flex items-center gap-1"
                  >
                    <span>{email}</span>
                    <button
                      className="text-red-500 hover:text-red-700"
                      onClick={() =>
                        setNewEvent((p) => ({
                          ...p,
                          participants: p.participants.filter(
                            (_, i) => i !== idx
                          ),
                        }))
                      }
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* --- Amount --- */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                {t.totalAmount}
              </label>
              {amountWarning && (
                <p className="text-red-600 mb-1">{amountWarning}</p>
              )}
              <input
                type="number"
                className="border w-full px-2 py-2 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={newEvent.totalAmount}
                onChange={handleTotalAmountChange}
              />
            </div>

            <div className="text-right mt-4">
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="mr-3 px-4 py-2 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleCreateEvent}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                {t.createEvent}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────── EDIT EVENT MODAL ─────────────────────── */}
      {isEditModalOpen && editEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-2">
          <div className="bg-white w-full max-w-lg rounded shadow p-4 md:p-6 relative text-sm">
            <button
              onClick={closeEditModal}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
            >
              &times;
            </button>

            <h3 className="text-base md:text-xl font-semibold mb-4">
              {t.modalTitleEdit} - {editEvent.name}
            </h3>

            {editErrorMsg && (
              <div className="mb-3 text-red-600 font-semibold">
                {editErrorMsg}
              </div>
            )}

            {/* Email input */}
            <div className="mb-3 relative">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                {t.participants}
              </label>
              <input
                type="text"
                className="border w-full px-2 py-2 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder={t.placeholderParticipants}
                value={editParticipantEmail}
                onChange={(e) =>
                  setEditParticipantEmail(e.target.value)
                }
                onFocus={() => setShowEditSuggestions(true)}
              />

              {showEditSuggestions && editEmailSuggestions.length > 0 && (
                <div className="absolute z-50 bg-white border border-gray-300 rounded mt-1 w-full max-h-32 overflow-y-auto shadow-md text-sm">
                  {editEmailSuggestions.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleSelectEditSuggestion(item)}
                      className="px-2 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      {formatEditSuggestion(item)}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Amount input */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                {t.amountCol}
              </label>
              <input
                type="number"
                className="border w-full px-2 py-2 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder={t.editParticipantAmountPlaceholder}
                value={editParticipantAmount}
                onChange={(e) => setEditParticipantAmount(e.target.value)}
              />
            </div>

            <button
              onClick={handleAddEditParticipant}
              className="mb-4 px-3 py-2 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm"
            >
              {t.addParticipantBtn}
            </button>

            {editNewParticipants.length > 0 && (
              <div className="mt-2">
                <p className="font-semibold mb-2">{t.participants}:</p>
                {editNewParticipants.map((p, i) => (
                  <div
                    key={i}
                    className="bg-blue-100 text-blue-600 px-2 py-1 rounded flex items-center justify-between mb-2"
                  >
                    <span>
                      {p.email} – {t.currencySymbol}
                      {p.amount}
                    </span>
                    <button
                      className="text-red-500 hover:text-red-700 ml-2"
                      onClick={() =>
                        setEditNewParticipants((prev) =>
                          prev.filter((_, idx) => idx !== i)
                        )
                      }
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="text-right mt-4">
              <button
                onClick={closeEditModal}
                className="mr-3 px-4 py-2 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                {t.saveChangesBtn}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────── TRACK EVENT MODAL ─────────────────────── */}
      {trackModalOpen && trackEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-2">
          <div className="bg-white w-full max-w-xl rounded shadow p-4 md:p-6 relative text-sm">
            <button
              onClick={closeTrackModal}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
            >
              &times;
            </button>

            <h3 className="text-base md:text-xl font-semibold mb-4">
              {t.trackModalTitle} - {trackEvent.name}
            </h3>

            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-gray-600 text-xs md:text-sm">
                <thead className="uppercase bg-gray-50 text-gray-700">
                  <tr>
                    <th className="px-2 md:px-4 py-2 md:py-3">
                      {t.participantCol}
                    </th>
                    <th className="px-2 md:px-4 py-2 md:py-3">
                      {t.amountCol}
                    </th>
                    <th className="px-2 md:px-4 py-2 md:py-3">
                      {t.statusCol}
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {trackParticipants.map((p, idx) => {
                    const displayAmount =
                      typeof p.amount === 'number'
                        ? p.amount
                        : Math.round(
                            (trackEvent.total_amount || 0) /
                              (trackEvent.num_participants || 1)
                          )

                    const paid = p.isPaid
                    const payLabel = paid ? t.paidStatus : t.pendingStatus
                    const payClass = paid
                      ? 'inline-block px-2 py-1 text-xs font-semibold bg-green-100 text-green-600 rounded'
                      : 'inline-block px-2 py-1 text-xs font-semibold bg-red-100 text-red-600 rounded'

                    return (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          {p.name ? `${p.name} - ${p.email}` : p.email}
                        </td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          {t.currencySymbol}
                          {displayAmount}
                        </td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <span className={payClass}>{payLabel}</span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="text-right mt-4">
              <button
                onClick={closeTrackModal}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─────────────────────── CANCEL CONFIRM MODAL ─────────────────── */}
      {cancelTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-2">
          <div className="bg-white w-full max-w-sm rounded shadow p-4 md:p-6 text-sm">
            <h4 className="text-base md:text-lg font-semibold mb-4">
              {t.confirmCancel}
            </h4>
            <p className="mb-6 text-gray-700">
              “{cancelTarget.name}”
            </p>
            <div className="text-right">
              <button
                onClick={cancelModalClose}
                className="mr-3 px-4 py-2 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
              >
                {t.cancel}
              </button>
              <button
                onClick={doCancelEvent}
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
              >
                {t.confirmYes}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SocialPage
